import type { NetWorthDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { DonutChart, type DonutSlice } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { SectionCard } from "../section-card.js";

/** Cents as euros. The same conversion every figure on this page makes, once, at the edge. */
const euros = (cents: string): number => Number(cents) / 100;

/**
 * WHAT NET WORTH IS MADE OF, as proportions.
 *
 * ==========================================================================================
 * THE FOUR SLICES ARE THE FOUR LAYERS, IN THE ORDER THE CHART STACKS THEM
 * ==========================================================================================
 *
 * Savings, investments, vehicles, equity — the same four `netWorthChart` draws and the same four
 * that sum to the headline, which is what makes a part-of-whole mark honest here at all. Order
 * matters: `palette.ts` is one list, so a reader who picks a slice out of this chart finds its band
 * in the area chart above by colour. Change the order in one place and the page tells a small lie in
 * two.
 *
 * **EQUITY AND NOT PROPERTY-MINUS-MORTGAGE AS TWO SLICES.** The mortgage is a liability, not an
 * asset class, and a pie has no way to draw a part that subtracts. Equity — value less the loan — is
 * the property pillar's actual contribution to net worth, and it is the figure the area chart bands
 * as well. The composition list beside the chart still spells out both halves for a reader checking
 * the arithmetic.
 *
 * ==========================================================================================
 * A HOUSEHOLD UNDER WATER GETS A SENTENCE, NOT A SHAPE
 * ==========================================================================================
 *
 * Equity goes negative when the mortgage exceeds the valuation, and `DonutChart` refuses to draw a
 * negative slice — the refusal is in the primitive so no consumer can reach the shape without it.
 * The sentence is supplied here because only this page knows what a negative part MEANS in its own
 * subject. `dashboard.md`'s standing rule: the most important sentence should not require reading a
 * shape.
 *
 * ==========================================================================================
 * IT FOLLOWS NO FILTER, AND SAYS SO
 * ==========================================================================================
 *
 * AC-D3 fixes the net worth section to the whole household: a per-member breakdown "would need
 * ownership on investments and properties that nothing records". The heading carries the scope for
 * the same reason the surplus chart beside it carries its window — since the filters moved into the
 * title row they look page-wide, and a section that does not obey them has to say so itself.
 */
export function NetWorthDonut({ netWorth }: { netWorth: NetWorthDto }): ReactNode {
  const t = useT();

  /*
   * EQUITY IS COMPUTED HERE, IN CENTS, and that is safe for the reason the composition list gives
   * for doing the same subtraction: it is the difference of two figures the server already resolved,
   * so it cannot disagree with what is printed beside it. `BigInt` rather than euros, so the slice
   * is exact and the share is computed from cents (NFR-005).
   */
  const equityCents = BigInt(netWorth.propertiesCents) - BigInt(netWorth.mortgagesCents);

  const slices: DonutSlice[] = [
    { key: "savings", label: t("nav.savings"), cents: BigInt(netWorth.savingsCents) },
    { key: "investments", label: t("nav.investments"), cents: BigInt(netWorth.investmentsCents) },
    { key: "vehicles", label: t("veh.nav"), cents: BigInt(netWorth.vehiclesCents) },
    { key: "equity", label: t("dash.pillar.equity"), cents: equityCents },
  ];

  return (
    <SectionCard
      title={t("dash.composition.heading")}
      hint={t("dash.composition.hint")}
      testId="net-worth-donut"
    >
      <DonutChart
        slices={slices}
        totalLabel={t("dash.netWorth")}
        emptyMessage={t("dash.composition.empty")}
        negativeMessage={t("dash.composition.underWater", {
          amount: formatEur(euros(netWorth.propertiesCents) - euros(netWorth.mortgagesCents)),
        })}
      />
    </SectionCard>
  );
}
